import React, { useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { FAMILIES, pick } from "../lib/industries";
import { SIZES, GOALS, PICK_TXT, L as Lb, sizeLabel, goalLabel } from "../lib/businessLabels";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export function ProfileEdit({ open, onOpenChange }) {
  const { data, setData } = useApp();
  const { lang } = useLang();
  const L = (m) => Lb(m, lang);
  const o = data?.onboarding || {};
  const [mSearch, setMSearch] = useState("");
  const [f, setF] = useState({
    business_name: o.business_name || "", country: o.country || "", city: o.city || "",
    activity_family: o.activity_family || "", activity_type: o.activity_type || "", custom_activity: o.custom_activity || "",
    company_size: o.company_size || "", employees: o.employees || "", main_goal: o.main_goal || "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = () => {
    if (!f.business_name.trim()) { toast.error("Le nom de l'entreprise est requis."); return; }
    setData((d) => ({ ...(d || {}), onboarding: { ...(d?.onboarding || {}), ...f } }));
    toast.success("Profil mis à jour !");
    onOpenChange(false);
  };

  const fam = FAMILIES.find((x) => x.id === f.activity_family);
  const metiers = fam ? fam.metiers.filter(([, lbl]) => pick(lbl, lang).toLowerCase().includes(mSearch.toLowerCase())) : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[hsl(var(--card))] border-white/10 max-w-lg max-h-[90vh] overflow-y-auto" data-testid="profile-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Modifier mon profil</DialogTitle>
          <DialogDescription className="text-xs">Mettez à jour vos informations métier. Elles personnalisent les conseils de l'IA.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div><Label className="text-xs">Nom de l'entreprise</Label><Input data-testid="profile-edit-name" value={f.business_name} onChange={(e) => set("business_name", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Pays</Label><Input data-testid="profile-edit-country" value={f.country} onChange={(e) => set("country", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
            <div><Label className="text-xs">Ville</Label><Input data-testid="profile-edit-city" value={f.city} onChange={(e) => set("city", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
          </div>

          <div><Label className="text-xs">Famille d'activité</Label>
            <Select value={f.activity_family} onValueChange={(v) => { set("activity_family", v); set("activity_type", ""); set("custom_activity", ""); setMSearch(""); }}>
              <SelectTrigger data-testid="profile-edit-family" className="mt-1.5 bg-white/5 border-white/10 h-11"><SelectValue placeholder={L(PICK_TXT.chooseFam)} /></SelectTrigger>
              <SelectContent>{FAMILIES.map((x) => <SelectItem key={x.id} value={x.id}>{pick(x.label, lang)}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {f.activity_family && f.activity_family !== "autre" && (
            <div><Label className="text-xs">Métier précis</Label>
              <Input data-testid="profile-edit-metier-search" value={mSearch} onChange={(e) => setMSearch(e.target.value)} placeholder="🔎 Rechercher votre activité…" className="mt-1.5 mb-2 bg-white/5 border-white/10 h-10" />
              <Select value={f.activity_type} onValueChange={(v) => set("activity_type", v)}>
                <SelectTrigger data-testid="profile-edit-metier" className="bg-white/5 border-white/10 h-11"><SelectValue placeholder={L(PICK_TXT.chooseTrade)} /></SelectTrigger>
                <SelectContent>{metiers.map(([id, lbl]) => <SelectItem key={id} value={pick(lbl, lang)}>{pick(lbl, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {f.activity_family === "autre" && (
            <div><Label className="text-xs">Décrivez votre activité</Label><Input data-testid="profile-edit-custom" value={f.custom_activity} onChange={(e) => set("custom_activity", e.target.value)} placeholder="Ex : atelier de céramique" className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Taille de l'entreprise</Label>
              <Select value={f.company_size} onValueChange={(v) => set("company_size", v)}>
                <SelectTrigger data-testid="profile-edit-size" className="mt-1.5 bg-white/5 border-white/10 h-11"><SelectValue placeholder={L(PICK_TXT.choose)} /></SelectTrigger>
                <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{sizeLabel(s, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Nombre de salariés</Label><Input data-testid="profile-edit-employees" type="number" value={f.employees} onChange={(e) => set("employees", e.target.value)} className="mt-1.5 bg-white/5 border-white/10 h-11" /></div>
          </div>

          <div><Label className="text-xs">Objectif commercial principal</Label>
            <Select value={f.main_goal} onValueChange={(v) => set("main_goal", v)}>
              <SelectTrigger data-testid="profile-edit-goal" className="mt-1.5 bg-white/5 border-white/10 h-11"><SelectValue placeholder={L(PICK_TXT.choose)} /></SelectTrigger>
              <SelectContent>{GOALS.map((g) => <SelectItem key={g} value={g}>{goalLabel(g, lang)}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Pour recalculer votre Business Score (présence digitale, rythme commercial), utilisez « Relancer l'analyse ».</p>
        </div>

        <DialogFooter className="gap-2">
          <Button data-testid="profile-edit-cancel" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full border-white/10 bg-white/5 h-10">Annuler</Button>
          <Button data-testid="profile-edit-save" onClick={save} className="rounded-full h-10 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn"><Save size={15} className="mr-1.5" /> Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
